import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight, MapPin, Pause, Play } from "lucide-react";
import { formatPrice } from "@/components/PropertyCard";

export default function FeaturedCarousel({ projects = [] }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = projects.length;
  const timerRef = useRef(null);

  useEffect(() => {
    if (paused || total <= 1) return;
    timerRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % total);
    }, 5500);
    return () => clearInterval(timerRef.current);
  }, [paused, total]);

  if (!total) return null;

  const next = () => setIndex((i) => (i + 1) % total);
  const prev = () => setIndex((i) => (i - 1 + total) % total);
  const current = projects[index];
  const heroImg = current.images?.[0] || "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=2400";

  return (
    <div data-testid="featured-carousel" className="relative">
      <div
        className="relative grid lg:grid-cols-12 gap-px bg-slate-100 border border-slate-100 rounded-2xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {/* Image */}
        <Link
          to={`/projects/${current.id}`}
          data-testid={`carousel-slide-${index}`}
          className="lg:col-span-7 relative aspect-[16/10] lg:aspect-auto bg-slate-100 overflow-hidden group"
        >
          <img
            key={current.id}
            src={heroImg}
            alt={current.name}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            style={{ animation: "fadeUp 600ms ease both" }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent" />
          <div className="absolute top-4 left-4">
            <span className="bg-[#D4A437] text-white text-[10px] tracking-[0.15em] uppercase px-3 py-1.5 font-bold rounded-md shadow-md">
              Featured · {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
            </span>
          </div>
        </Link>

        {/* Detail */}
        <div className="lg:col-span-5 bg-white p-8 md:p-12 flex flex-col justify-between" key={`detail-${current.id}`} style={{ animation: "fadeUp 500ms ease both" }}>
          <div>
            <div className="overline mb-3 flex items-center gap-2">
              <MapPin className="w-3 h-3" /> {current.locality}, {current.city}
            </div>
            <h3 className="font-serif text-3xl md:text-4xl text-slate-900 font-bold leading-tight">
              {current.name}
            </h3>
            <div className="text-sm text-slate-500 mt-2">By <span className="text-slate-900 font-medium">{current.developer}</span></div>
            <p className="text-slate-600 mt-5 text-sm leading-relaxed line-clamp-3">{current.description}</p>

            <div className="mt-7 grid grid-cols-3 gap-3">
              <div className="bg-amber-50 rounded-xl py-3 px-4">
                <div className="text-[10px] tracking-wider text-[#B88C2E] font-bold uppercase mb-1">Starting</div>
                <div className="font-bold text-lg text-[#D4A437]">{formatPrice(current.price_min, current.currency)}</div>
              </div>
              <div className="bg-slate-50 rounded-xl py-3 px-4">
                <div className="text-[10px] tracking-wider text-slate-500 font-bold uppercase mb-1">BHK</div>
                <div className="font-bold text-lg text-slate-900">{current.bhk?.length ? current.bhk.join(", ") : "—"}</div>
              </div>
              <div className="bg-slate-50 rounded-xl py-3 px-4">
                <div className="text-[10px] tracking-wider text-slate-500 font-bold uppercase mb-1">Area</div>
                <div className="font-bold text-lg text-slate-900">{current.area_min}-{current.area_max}</div>
              </div>
            </div>
          </div>

          <Link
            to={`/projects/${current.id}`}
            data-testid={`carousel-cta-${index}`}
            className="mt-8 inline-flex items-center justify-center gap-2 bg-[#D4A437] hover:bg-[#B88C2E] text-white text-sm font-semibold px-6 py-3 rounded-lg transition-colors shadow-md"
          >
            View Project <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Controls */}
        {total > 1 && (
          <>
            <button
              onClick={prev}
              data-testid="carousel-prev"
              aria-label="Previous"
              className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/95 hover:bg-[#D4A437] hover:text-white text-slate-900 rounded-full transition-all flex items-center justify-center shadow-lg backdrop-blur"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={next}
              data-testid="carousel-next"
              aria-label="Next"
              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/95 hover:bg-[#D4A437] hover:text-white text-slate-900 rounded-full transition-all flex items-center justify-center shadow-lg backdrop-blur"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </>
        )}
      </div>

      {/* Footer: dots + pause */}
      {total > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="flex items-center gap-2" data-testid="carousel-dots">
            {projects.map((_, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                aria-label={`Go to slide ${i + 1}`}
                data-testid={`carousel-dot-${i}`}
                className={`h-1.5 rounded-full transition-all ${i === index ? "bg-[#D4A437] w-10" : "bg-slate-300 w-6 hover:bg-slate-400"}`}
              />
            ))}
          </div>
          <button
            onClick={() => setPaused((v) => !v)}
            data-testid="carousel-pause-toggle"
            aria-label={paused ? "Play" : "Pause"}
            className="text-xs font-semibold text-slate-500 hover:text-[#D4A437] inline-flex items-center gap-2 tracking-wider uppercase transition-colors"
          >
            {paused ? <><Play className="w-3 h-3" /> Play</> : <><Pause className="w-3 h-3" /> Pause</>}
          </button>
        </div>
      )}
    </div>
  );
}
